/**
 * Admin Portal — Role Management Page
 * Assign roles/permissions to employees and SMEs via dropdowns.
 */
import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Table, TableHead, TableRow, TableCell, TableBody,
  Chip, IconButton, Button, Alert, Autocomplete, TextField,
  Card, CardContent, Divider, Stack, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  Checkbox, FormControlLabel, FormGroup,
} from '@mui/material';
import { PersonAdd, CheckCircle, PersonOff, Delete, PersonOutline, LibraryBooks } from '@mui/icons-material';
import { authFetch } from '../../../api/authFetch';
import RefreshButton from '../../../components/RefreshButton';

interface UserRecord {
  id: string;
  name: string;
  email: string;
  role: string;
  secondary_roles: string[];
  is_active: boolean;
  organization_domain: string;
}

/** Roles that can be assigned as secondary (multi-role) capabilities. */
const SECONDARY_ROLE_OPTIONS = [
  { value: 'domain_creator', label: 'Domain Creator', description: 'Create and manage custom assessment domains' },
  { value: 'assessment_author', label: 'Assessment Author', description: 'Author assessment content and domains' },
  { value: 'content_manager', label: 'Content Manager', description: 'Manage assessment content and domains' },
];

const AVAILABLE_ROLES = [
  { value: 'super_admin', label: 'Super Admin', description: 'Full platform access' },
  { value: 'user_admin', label: 'User Admin', description: 'User management only' },
  { value: 'engagement_manager', label: 'Engagement Manager', description: 'Leads client engagements, validates reports' },
  { value: 'engagement_lead', label: 'Engagement Lead', description: 'Senior client engagement lead' },
  { value: 'consultant', label: 'Consultant', description: 'KPMG consultant' },
  { value: 'domain_creator', label: 'Domain Creator', description: 'Create and manage custom assessment domains' },
  { value: 'assessment_author', label: 'Assessment Author', description: 'Author assessment content and domains' },
  { value: 'content_manager', label: 'Content Manager', description: 'Manage assessment content and domains' },
  { value: 'client', label: 'Client', description: 'External client access' },
  // Domain-scoped EM roles (one per assessment category)
  { value: 'em_security_compliance', label: 'EM – Security & Compliance', description: 'EM for Security & Compliance assessments' },
  { value: 'em_ai_analytics', label: 'EM – AI & Analytics', description: 'EM for AI & Analytics assessments' },
  { value: 'em_development_operations', label: 'EM – Development & Operations', description: 'EM for Development & Operations assessments' },
  { value: 'em_cloud_strategy_adoption', label: 'EM – Cloud Strategy & Adoption', description: 'EM for Cloud Strategy & Adoption assessments' },
  { value: 'em_productivity_collaboration', label: 'EM – Productivity & Collaboration', description: 'EM for Productivity & Collaboration assessments' },
  { value: 'em_baseline_architecture', label: 'EM – Baseline Architecture', description: 'EM for Baseline Architecture assessments' },
  // Security & Compliance
  { value: 'sme_cloud_security_adoption', label: 'SME – Cloud Security Adoption', description: 'Cloud Security Adoption expert' },
  { value: 'sme_zero_trust_maturity', label: 'SME – Zero Trust Maturity', description: 'Zero Trust expert' },
  { value: 'sme_data_security_copilot', label: 'SME – Data Security Copilot', description: 'Data Security Copilot expert' },
  // Cloud Strategy & Adoption
  { value: 'sme_cloud_maturity', label: 'SME – Cloud Maturity', description: 'Cloud Maturity expert' },
  { value: 'sme_azure_landing_zone', label: 'SME – Azure Landing Zone', description: 'Azure Landing Zone expert' },
  { value: 'sme_azure_vmware_solution', label: 'SME – Azure VMware Solution', description: 'Azure VMware Solution expert' },
  { value: 'sme_waf_assessment', label: 'SME – Well-Architected Framework', description: 'WAF assessment expert' },
  { value: 'sme_caf_assessment', label: 'SME – Cloud Adoption Framework', description: 'CAF assessment expert' },
  // AI & Analytics
  { value: 'sme_ai_readiness', label: 'SME – AI Readiness', description: 'AI Readiness expert' },
  { value: 'sme_ai_engineer_skills', label: 'SME – AI Engineer Skills', description: 'AI Engineering expert' },
  { value: 'sme_agentic_ai_readiness', label: 'SME – Agentic AI Readiness', description: 'Agentic AI expert' },
  { value: 'sme_fabric_analytics_journey', label: 'SME – Fabric Analytics Journey', description: 'Fabric Analytics expert' },
  { value: 'sme_genai_maturity', label: 'SME – GenAI Maturity', description: 'GenAI Maturity expert' },
  // Development & Operations
  { value: 'sme_devops_capability', label: 'SME – DevOps Capability', description: 'DevOps expert' },
  { value: 'sme_app_data_modernization', label: 'SME – App Data Modernization', description: 'App Modernization expert' },
  { value: 'sme_azure_integration_service', label: 'SME – Azure Integration Service', description: 'Azure Integration expert' },
  { value: 'sme_devsecops_capability', label: 'SME – DevSecOps Capability', description: 'DevSecOps expert' },
  // Productivity & Collaboration
  { value: 'sme_m365_copilot_readiness', label: 'SME – M365 Copilot Readiness', description: 'M365 Copilot expert' },
  { value: 'sme_copilot_studio_readiness', label: 'SME – Copilot Studio Readiness', description: 'Copilot Studio expert' },
  { value: 'sme_power_platform_adoption', label: 'SME – Power Platform Adoption', description: 'Power Platform Adoption expert' },
  { value: 'sme_power_platform_well_architected', label: 'SME – Power Platform Well-Architected', description: 'Power Platform Architecture expert' },
  { value: 'sme_d365_fo_readiness', label: 'SME – Dynamics 365 F&O', description: 'Dynamics 365 Finance & Operations expert' },
  { value: 'sme_d365_sales_customer_field', label: 'SME – Dynamics 365 Sales & Field Service', description: 'Dynamics 365 CRM expert' },
  { value: 'sme_d365_contact_center', label: 'SME – Dynamics 365 Contact Center', description: 'Dynamics 365 Contact Center expert' },
  // Baseline Architecture
  { value: 'sme_baseline_architecture', label: 'SME – Baseline Architecture', description: 'Baseline Architecture expert' },
];

const RoleManagement: React.FC = () => {
  const [users, setUsers] = useState<UserRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Assignment form state
  const [selectedUser, setSelectedUser] = useState<UserRecord | null>(null);
  const [selectedRole, setSelectedRole] = useState<typeof AVAILABLE_ROLES[0] | null>(null);
  const [assigning, setAssigning] = useState(false);

  // Deactivation/removal dialog state
  const [confirmDialog, setConfirmDialog] = useState<{ user: UserRecord; action: 'deactivate' | 'remove' } | null>(null);

  // Secondary roles dialog state
  const [secRolesUser, setSecRolesUser] = useState<UserRecord | null>(null);
  const [secRolesSelection, setSecRolesSelection] = useState<string[]>([]);
  const [secRolesSaving, setSecRolesSaving] = useState(false);

  const fetchUsers = () => {
    setLoading(true);
    authFetch('/api/admin/users')
      .then((r) => r.json())
      .then((data) => { setUsers(data.users || []); })
      .catch(() => setError('Failed to load users'))
      .finally(() => { setLastRefreshed(new Date()); setLoading(false); });
  };

  useEffect(() => { fetchUsers(); }, []);

  const handleAssignRole = async () => {
    if (!selectedUser || !selectedRole) return;
    setAssigning(true);
    setError('');
    setSuccess('');
    try {
      const res = await authFetch(`/api/admin/users/${selectedUser.id}/role`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role: selectedRole.value }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to update role');
      }
      setSuccess(`Assigned "${selectedRole.label}" to ${selectedUser.name || selectedUser.email}`);
      setUsers((prev) => prev.map((u) =>
        u.id === selectedUser.id ? { ...u, role: selectedRole.value } : u
      ));
      setSelectedUser(null);
      setSelectedRole(null);
    } catch (err: any) {
      setError(err.message || 'Failed to assign role. Check permissions.');
    } finally {
      setAssigning(false);
    }
  };

  const getRoleColor = (role: string) => {
    if (role.includes('admin')) return 'error';
    if (role.startsWith('sme_')) return 'success';
    if (role.startsWith('em_')) return 'warning';
    if (['engagement_manager', 'engagement_lead', 'consultant'].includes(role)) return 'info';
    if (['domain_creator', 'assessment_author', 'content_manager'].includes(role)) return 'secondary';
    return 'default';
  };

  const openSecRolesDialog = (user: UserRecord) => {
    setSecRolesUser(user);
    setSecRolesSelection(user.secondary_roles || []);
  };

  const handleSaveSecondaryRoles = async () => {
    if (!secRolesUser) return;
    setSecRolesSaving(true);
    setError('');
    setSuccess('');
    try {
      const res = await authFetch(`/api/admin/users/${secRolesUser.id}/secondary-roles`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ secondary_roles: secRolesSelection }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to update secondary roles');
      setSuccess(`Updated secondary roles for ${secRolesUser.name || secRolesUser.email}`);
      setUsers((prev) => prev.map((u) =>
        u.id === secRolesUser.id ? { ...u, secondary_roles: secRolesSelection } : u
      ));
      setSecRolesUser(null);
    } catch (err: any) {
      setError(err.message || 'Failed to update secondary roles');
    } finally {
      setSecRolesSaving(false);
    }
  };

  const handleDeactivateOrRemove = async () => {
    if (!confirmDialog) return;
    const { user, action } = confirmDialog;
    setError('');
    setSuccess('');
    try {
      const endpoint = action === 'deactivate'
        ? `/api/admin/users/${user.id}/deactivate`
        : `/api/admin/users/${user.id}`;
      const method = action === 'deactivate' ? 'PUT' : 'DELETE';
      const res = await authFetch(endpoint, { method });
      if (!res.ok) throw new Error();
      if (action === 'deactivate') {
        setSuccess(`${user.name || user.email} has been deactivated`);
        setUsers((prev) => prev.map((u) => u.id === user.id ? { ...u, is_active: false } : u));
      } else {
        setSuccess(`${user.name || user.email} has been permanently removed`);
        setUsers((prev) => prev.filter((u) => u.id !== user.id));
      }
    } catch {
      setError(`Failed to ${action} user.`);
    } finally {
      setConfirmDialog(null);
    }
  };

  const handleReactivate = async (user: UserRecord) => {
    setError('');
    setSuccess('');
    try {
      const res = await authFetch(`/api/admin/users/${user.id}/activate`, { method: 'PUT' });
      if (!res.ok) throw new Error();
      setSuccess(`${user.name || user.email} has been re-activated`);
      setUsers((prev) => prev.map((u) => u.id === user.id ? { ...u, is_active: true } : u));
    } catch {
      setError('Failed to re-activate user.');
    }
  };

  const getRoleLabel = (roleValue: string) => {
    const found = AVAILABLE_ROLES.find((r) => r.value === roleValue);
    return found ? found.label : roleValue.replace(/_/g, ' ');
  };

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
        Role Management
      </Typography>
        <RefreshButton onRefresh={fetchUsers} loading={loading} lastRefreshed={lastRefreshed} />
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Assign roles and permissions to employees and SMEs.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Role Assignment Card */}
      <Card sx={{ mb: 4, border: '1px solid', borderColor: 'divider' }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            <PersonAdd sx={{ mr: 1, verticalAlign: 'middle' }} />
            Assign Role
          </Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start">
            <Autocomplete
              options={users}
              value={selectedUser}
              onChange={(_, val) => setSelectedUser(val)}
              getOptionLabel={(u) => `${u.name || 'Unnamed'} (${u.email})`}
              renderOption={(props, u) => (
                <li {...props} key={u.id}>
                  <Box>
                    <Typography variant="body2" fontWeight={600}>{u.name || 'Unnamed'}</Typography>
                    <Typography variant="caption" color="text.secondary">{u.email} — {u.organization_domain}</Typography>
                  </Box>
                </li>
              )}
              sx={{ minWidth: 320, flex: 1 }}
              renderInput={(params) => (
                <TextField {...params} label="Select Employee / SME" placeholder="Search by name or email..." />
              )}
              loading={loading}
            />
            <Autocomplete
              options={AVAILABLE_ROLES}
              value={selectedRole}
              onChange={(_, val) => setSelectedRole(val)}
              getOptionLabel={(r) => r.label}
              renderOption={(props, r) => (
                <li {...props} key={r.value}>
                  <Box>
                    <Typography variant="body2" fontWeight={600}>{r.label}</Typography>
                    <Typography variant="caption" color="text.secondary">{r.description}</Typography>
                  </Box>
                </li>
              )}
              sx={{ minWidth: 280, flex: 1 }}
              renderInput={(params) => (
                <TextField {...params} label="Select Role" placeholder="Choose a role..." />
              )}
            />
            <Button
              variant="contained"
              startIcon={<CheckCircle />}
              onClick={handleAssignRole}
              disabled={!selectedUser || !selectedRole || assigning}
              sx={{ height: 56, px: 4 }}
            >
              {assigning ? 'Assigning...' : 'Assign Role'}
            </Button>
          </Stack>
          {selectedUser && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Current role: <Chip label={getRoleLabel(selectedUser.role)} size="small" color={getRoleColor(selectedUser.role) as any} />
            </Typography>
          )}
        </CardContent>
      </Card>

      <Divider sx={{ mb: 3 }} />

      {/* Current Assignments Table */}
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
        Current Role Assignments
      </Typography>
      <Paper>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Organization</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Primary Role</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Secondary Roles</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7}>Loading...</TableCell></TableRow>
            ) : users.length === 0 ? (
              <TableRow><TableCell colSpan={7} align="center">No users found</TableCell></TableRow>
            ) : (
              users.map((u) => (
                <TableRow key={u.id} hover sx={{ cursor: 'pointer', opacity: u.is_active === false ? 0.5 : 1 }} onClick={() => { setSelectedUser(u); }}>
                  <TableCell>{u.name || '—'}</TableCell>
                  <TableCell>{u.email}</TableCell>
                  <TableCell>{u.organization_domain}</TableCell>
                  <TableCell>
                    <Chip label={getRoleLabel(u.role)} size="small" color={getRoleColor(u.role) as any} />
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    {u.secondary_roles?.length > 0 ? (
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {u.secondary_roles.map((sr) => (
                          <Chip key={sr} label={getRoleLabel(sr)} size="small" variant="outlined" color="secondary" />
                        ))}
                      </Stack>
                    ) : (
                      <Typography variant="caption" color="text.disabled">—</Typography>
                    )}
                    <Tooltip title="Manage secondary roles">
                      <IconButton size="small" sx={{ ml: 0.5 }} onClick={() => openSecRolesDialog(u)}>
                        <LibraryBooks fontSize="small" color="action" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={u.is_active === false ? 'Inactive' : 'Active'}
                      size="small"
                      color={u.is_active === false ? 'default' : 'success'}
                      variant={u.is_active === false ? 'outlined' : 'filled'}
                    />
                  </TableCell>
                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    {u.is_active === false ? (
                      <Tooltip title="Re-activate user">
                        <IconButton size="small" color="success" onClick={() => handleReactivate(u)}>
                          <PersonOutline fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : u.role !== 'super_admin' ? (
                      <Tooltip title="Deactivate user">
                        <IconButton size="small" color="warning" onClick={() => setConfirmDialog({ user: u, action: 'deactivate' })}>
                          <PersonOff fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : null}
                    {u.role !== 'super_admin' && (
                      <Tooltip title="Permanently remove user">
                        <IconButton size="small" color="error" onClick={() => setConfirmDialog({ user: u, action: 'remove' })}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* Confirmation Dialog */}
      <Dialog open={!!confirmDialog} onClose={() => setConfirmDialog(null)}>
        <DialogTitle>
          {confirmDialog?.action === 'deactivate' ? 'Deactivate User' : 'Permanently Remove User'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmDialog?.action === 'deactivate'
              ? `This will deactivate "${confirmDialog.user.name || confirmDialog.user.email}". They will no longer be able to log in, but their data (assessments, reports) will be preserved.`
              : `This will permanently delete "${confirmDialog?.user.name || confirmDialog?.user.email}" and cannot be undone. Their assessments and reports will remain but will no longer be linked to this user.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(null)}>Cancel</Button>
          <Button
            variant="contained"
            color={confirmDialog?.action === 'deactivate' ? 'warning' : 'error'}
            onClick={handleDeactivateOrRemove}
          >
            {confirmDialog?.action === 'deactivate' ? 'Deactivate' : 'Remove Permanently'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Secondary Roles Dialog */}
      <Dialog open={!!secRolesUser} onClose={() => setSecRolesUser(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>
          Manage Secondary Roles
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Assign additional domain management capabilities to <strong>{secRolesUser?.name || secRolesUser?.email}</strong>.
            Secondary roles grant access to the Domain Management Portal alongside their primary role
            ({secRolesUser ? getRoleLabel(secRolesUser.role) : ''}).
          </DialogContentText>
          <FormGroup>
            {SECONDARY_ROLE_OPTIONS.map((opt) => (
              <FormControlLabel
                key={opt.value}
                control={
                  <Checkbox
                    checked={secRolesSelection.includes(opt.value)}
                    onChange={(e) => {
                      setSecRolesSelection((prev) =>
                        e.target.checked
                          ? [...prev, opt.value]
                          : prev.filter((r) => r !== opt.value)
                      );
                    }}
                    sx={{ color: '#098E7E', '&.Mui-checked': { color: '#098E7E' } }}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={600}>{opt.label}</Typography>
                    <Typography variant="caption" color="text.secondary">{opt.description}</Typography>
                  </Box>
                }
              />
            ))}
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSecRolesUser(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveSecondaryRoles}
            disabled={secRolesSaving}
            sx={{ bgcolor: '#098E7E', '&:hover': { bgcolor: '#077a6c' } }}
          >
            {secRolesSaving ? 'Saving...' : 'Save Secondary Roles'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RoleManagement;
